'use client'

import * as React from 'react'
import { buildOfferHref } from '@/lib/offer-href'
import { readClickContextFromLocation } from '@/lib/click-context-decode'
import { useIsHydrated } from '@/hooks/use-is-hydrated'

/**
 * SignedOfferLink — FIX-13b (plan §3.8 [A-7]) click-path signing.
 *
 * Renders a plain <a> to /r/{offerId} and, on first hover/touch/focus/
 * pointerdown, fetches a fresh cid/ts/hmac from /api/r-sign and rewrites the
 * href so the click navigation carries the signature. If the signing call
 * hasn't resolved (or fails), the unsigned fallback href is used — the
 * redirect handler flags unsigned clicks but never blocks them while
 * CLICK_SIGNING_ENFORCE is off, so there is zero user-visible change.
 *
 * FRIDGE-15 leg 1 — the unsigned fallback href ALSO carries the client-minted
 * cid (and pos): the mount effect below resolves the same cid `sign()` would
 * mint and rewrites the fallback, so a click that fires before/without the
 * signing fetch (no hover on touch devices, signing endpoint slow/failed)
 * still lands in the clicks table with a joinable cid. The /r/ handler reads
 * ?cid= unconditionally (app/r/[offerId]/route.ts — cid is written on the
 * click row with signature_status='unsigned'), so no server change is needed.
 * Server render never includes cid (it is client-minted), which keeps SSR and
 * first client render identical — no hydration mismatch.
 *
 * cid resolution: PostHog distinct_id from the posthog-js cookie when present,
 * else a persisted first-party random id in localStorage.
 */

const SIGNATURE_TTL_MS = 4 * 60 * 1000 // handler accepts ±5 min; refresh before expiry
const CID_STORAGE_KEY = 'fridge_cid'

/**
 * The path of the page this link is rendered on, for `?sp=` (FRIDGE-312).
 *
 * Read from `window.location` rather than `usePathname()` on purpose: it is
 * resolved in the SAME mount effect as the cid, so server markup and the first
 * client render stay identical (no hydration mismatch) and the fallback href
 * grows exactly once. Returns null on the server and in any environment with no
 * location — the click then behaves precisely as it does today.
 *
 * Query and hash are dropped here as well as server-side: the Referer-derived
 * value this stands in for is `URL.pathname`, and the two must agree or the
 * column means two different things.
 */
export function getClickSourcePage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const path = window.location?.pathname
    return typeof path === 'string' && path.startsWith('/') ? path : null
  } catch {
    return null
  }
}

export function getClickCid(): string | null {
  if (typeof document === 'undefined') return null
  // 1) PostHog distinct_id from the posthog-js cookie (ph_<token>_posthog)
  try {
    const cookie = document.cookie
      .split('; ')
      .find((c) => /^ph_[^=]+_posthog=/.test(c))
    if (cookie) {
      const raw = decodeURIComponent(cookie.slice(cookie.indexOf('=') + 1))
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed.distinct_id === 'string' && parsed.distinct_id) {
        return parsed.distinct_id
      }
    }
  } catch {
    // fall through to first-party id
  }
  // 2) Persisted first-party random id
  try {
    let cid = window.localStorage.getItem(CID_STORAGE_KEY)
    if (!cid) {
      cid =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
      window.localStorage.setItem(CID_STORAGE_KEY, cid)
    }
    return cid
  } catch {
    return null
  }
}

/**
 * The /r/ href builder lives in lib/offer-href.ts (framework-free, so server
 * components and routines can call it too — FRIDGE-276). Re-exported here so
 * existing client-side importers keep working; this component is the ONLY
 * renderer of an /r/ anchor and the builder is the ONLY author of its shape.
 */
export { buildOfferHref } from '@/lib/offer-href'

export interface SignedOfferLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  offerId: string
  productId?: string
  /** Click-position attribution (e.g. 'verdict', 'reviews-inline') — carried
   *  on both the unsigned fallback and the signed URL. */
  pos?: string
  children: React.ReactNode
}

export function SignedOfferLink({
  offerId,
  productId,
  pos,
  children,
  rel,
  onMouseEnter,
  onTouchStart,
  onFocus,
  onPointerDown,
  ...rest
}: SignedOfferLinkProps) {
  // FRIDGE-15 leg 1: carry the client-minted cid on the unsigned fallback too.
  // State starts null so SSR markup and the first client render agree (no
  // hydration mismatch); the mount effect then resolves the persistent cid —
  // the SAME id sign() sends to /api/r-sign — and the fallback href picks it
  // up. Clicks that beat or bypass the signing fetch stay joinable on cid.
  const hydrated = useIsHydrated()
  // FRIDGE-312: the page path rides the same mount effect as the cid. The
  // Referer header is absent on the majority of unattributed human clicks, and
  // `?sp=` is the only thing that can name the page when it is. Same lifecycle,
  // same hydration guarantee, same "analytics-only" trust level as `pos`.
  // Both are read AFTER hydration and DERIVED, not pushed into state from an
  // effect — the shape react-hooks/set-state-in-effect flags. The lifecycle and
  // the hydration guarantee described above are unchanged: null on the server
  // and through the hydration pass, real values on the render straight after.
  const fallbackCid = React.useMemo(() => (hydrated ? getClickCid() : null), [hydrated])
  const sourcePage = React.useMemo(() => (hydrated ? getClickSourcePage() : null), [hydrated])
  // FRIDGE-818: the server-signed click context the visitor arrived with
  // (`?cx=` on this page's URL, minted by the ChatGPT App's tool response).
  // Same lifecycle and trust level as `sp` — read after hydration, carried
  // onto the /r/ href so the click row and the affiliate_click event can say
  // "this began in ChatGPT". The page cannot verify it; the /r/ route does.
  const clickContext = React.useMemo(() => (hydrated ? readClickContextFromLocation() : null), [hydrated])
  const baseHref = buildOfferHref(offerId, { productId, pos, sourcePage, cid: fallbackCid, clickContext })
  const [signedHref, setSignedHref] = React.useState<string | null>(null)
  const signedAtRef = React.useRef(0)
  const inFlightRef = React.useRef(false)
  const anchorRef = React.useRef<HTMLAnchorElement | null>(null)

  const sign = React.useCallback(() => {
    if (inFlightRef.current) return
    if (signedAtRef.current && Date.now() - signedAtRef.current < SIGNATURE_TTL_MS) return
    inFlightRef.current = true
    const cid = getClickCid()
    const qs = new URLSearchParams({ offer_id: offerId })
    if (cid) qs.set('cid', cid)
    fetch(`/api/r-sign?${qs.toString()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { cid?: string; ts?: number; hmac?: string } | null) => {
        if (data && data.ts && data.hmac) {
          signedAtRef.current = Date.now()
          setSignedHref(
            buildOfferHref(offerId, {
              productId,
              pos,
              // Re-read rather than closing over state: sign() can fire before
              // the mount effect has landed (a fast hover on a slow paint), and
              // a signed href that dropped the page would be a REGRESSION
              // against the unsigned fallback.
              sourcePage: getClickSourcePage(),
              cid: data.cid,
              ts: data.ts,
              hmac: data.hmac,
              clickContext: readClickContextFromLocation(),
            }),
          )
        }
      })
      .catch(() => {
        // Unsigned fallback — the /r/ handler flags but never blocks (flag off)
      })
      .finally(() => {
        inFlightRef.current = false
      })
  }, [offerId, productId, pos])

  // Click-integrity Phase 3 leg A — viewport pre-signing.
  //
  // Interaction-triggered signing loses a race the click itself starts: the
  // browser navigates ~50–150ms after pointerdown, usually before the
  // /api/r-sign round trip resolves, so ~99% of genuine human clicks went out
  // `unsigned` (1–9 valid/day of ~700) and CLICK_SIGNING_ENFORCE could never
  // be flipped. Signing when the anchor first scrolls into view (200px early)
  // removes the race entirely: by the time a person can click, the href is
  // already signed. The interaction handlers stay as the refresh path — sign()
  // no-ops while the current signature is fresh and re-mints when it is stale.
  //
  // Deliberate trade-off, paired with the /r/ replay guard (CLICK_REPLAY_GUARD,
  // route leg C): pre-signing places signed hrefs into the live DOM, which a
  // hydrating scraper can harvest and replay within the ±300s window. The
  // replay guard makes each signature single-use, so a harvested href spends
  // its one validity on the harvester and every fleet replay reads `invalid`.
  //
  // Guarded for environments without IntersectionObserver (node-env jest —
  // the mount-test harness fires effects inline; this one must no-op there).
  React.useEffect(() => {
    if (!hydrated) return
    const el = anchorRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            sign()
            io.disconnect()
            break
          }
        }
      },
      { rootMargin: '200px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [hydrated, sign])

  return (
    <a
      ref={anchorRef}
      href={signedHref ?? baseHref}
      rel={rel ?? 'nofollow noopener sponsored'}
      onMouseEnter={(e) => {
        sign()
        onMouseEnter?.(e)
      }}
      onTouchStart={(e) => {
        sign()
        onTouchStart?.(e)
      }}
      onFocus={(e) => {
        sign()
        onFocus?.(e)
      }}
      onPointerDown={(e) => {
        sign()
        onPointerDown?.(e)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
